import { useState, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { CompanyWithRelations } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import EditCompanyModal from "@/components/modals/edit-company-modal";
import FMCSACarriersBulkModal from "@/components/modals/fmcsa-carriers-bulk-modal";
import CreateWorkOrderModal from "@/components/modals/create-work-order-modal";
import { formatPhoneNumber } from "@/lib/utils";
import NotesSection from "@/components/notes/notes-section";
import ContactsSection from "@/components/contacts/contacts-section";
import CompanyContactsSection from "@/components/company-contacts/company-contacts-section";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Building2, Mail, Phone, MapPin, User, Edit, X, Users, Truck, FileText, Plus, Shield, Search, Calendar, Download, AlertCircle } from "lucide-react";
import { useLocation } from "wouter";
import { format } from "date-fns";

interface CompanyDetailsProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
}

export default function CompanyDetails({ open, onClose, companyId }: CompanyDetailsProps) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, setLocation] = useLocation();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFMCSABulkModal, setShowFMCSABulkModal] = useState(false);
  const [showCreateWorkOrderModal, setShowCreateWorkOrderModal] = useState(false);
  const [workOrderSearchTerm, setWorkOrderSearchTerm] = useState("");
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState("all");
  
  const { data: company, isLoading } = useQuery<CompanyWithRelations>({
    queryKey: ["/api/companies", companyId],
    enabled: open && !!companyId,
    retry: false,
  });

  // Fetch company contacts to find primary contact
  const { data: companyContacts = [] } = useQuery<any[]>({
    queryKey: [`/api/companies/${companyId}/contacts`],
    enabled: open && !!companyId,
  });

  // Fetch users for the create work order modal
  const { data: users = [] } = useQuery<any[]>({
    queryKey: ["/api/assignable-users"],
    enabled: open && !!companyId,
  });

  // Find the primary contact for this company (uses isPrimary boolean only)
  const primaryContact = useMemo(() => {
    const primaryRelation = companyContacts.find((cc: any) => cc.isPrimary === true);
    return primaryRelation?.contact;
  }, [companyContacts]);

  const updateCompanyMutation = useMutation({
    mutationFn: async (companyData: any) => {
      await apiRequest("PUT", `/api/companies/${companyId}`, companyData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      toast({
        title: "Success",
        description: "Company updated successfully",
      });
      setShowEditModal(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update company",
        variant: "destructive",
      });
    },
  });

  const handleUpdateCompany = (companyData: any) => {
    updateCompanyMutation.mutate(companyData);
  };

  // Create work order mutation
  const createWorkOrderMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/work-orders", data);
      return await response.json();
    },
    onSuccess: (createdWorkOrder) => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      toast({
        title: "Success",
        description: "Work order created successfully",
      });
      setShowCreateWorkOrderModal(false);
      // Navigate to the newly created work order details page
      if (createdWorkOrder?.id) {
        setLocation(`/work-orders/${createdWorkOrder.id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create work order",
        variant: "destructive",
      });
    },
  });

  const filteredWorkOrders = useMemo(() => {
    if (!company?.workOrders) return [];
    
    return company.workOrders.filter((workOrder: any) => {
      const matchesSearch = workOrderSearchTerm === "" || 
        workOrder.workOrderNumber?.toLowerCase().includes(workOrderSearchTerm.toLowerCase()) ||
        workOrder.serviceType?.toLowerCase().includes(workOrderSearchTerm.toLowerCase());
      
      const matchesStatus = workOrderStatusFilter === "all" || 
        (workOrderStatusFilter === "active" && workOrder.status !== "closed" && workOrder.status !== "cancelled") ||
        workOrder.status === workOrderStatusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [company?.workOrders, workOrderSearchTerm, workOrderStatusFilter]);

  const formatCompanyType = (type: string) => {
    return type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCompanyTypeColor = (type: string) => {
    switch (type) {
      case "service_bureau":
        return "bg-blue-100 text-blue-800";
      case "direct_client":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatLoginGovAuthMethod = (method: string) => {
    const methods = {
      'face_touch_unlock': 'Face or touch unlock',
      'authentication_application': 'Authentication application',
      'security_key': 'Security key',
      'text_call': 'Text message or call',
      'backup_code': 'Backup code',
      'piv_cac': 'PIV or CAC',
      'none': 'None',
      'unknown': 'Unknown'
    };
    return methods[method as keyof typeof methods] || method;
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl">
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Loading company details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!company) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl">
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Company not found</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent 
          className="sm:max-w-4xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            if (showCreateWorkOrderModal || showEditModal) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            if (showCreateWorkOrderModal || showEditModal) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (showCreateWorkOrderModal || showEditModal) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {company.name}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Badge className={getCompanyTypeColor(company.type)}>
                  {formatCompanyType(company.type)}
                </Badge>
                <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="carriers">Carriers</TabsTrigger>
              <TabsTrigger value="work-orders">Work Orders</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Company Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.name}</p>
                      {(company as any).dotProfile?.dbaName && (
                        <p className="text-sm text-gray-600">DBA: {(company as any).dotProfile?.dbaName}</p>
                      )}
                      <p className="text-sm text-gray-500">{formatCompanyType(company.type)}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.email || "Not provided"}</p>
                      <p className="text-sm text-gray-500">Email</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{formatPhoneNumber(company.phone) || "Not provided"}</p>
                      <p className="text-sm text-gray-500">Phone</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="font-medium">{company.address || "Not provided"}</p>
                      <p className="text-sm text-gray-500">Address</p>
                    </div>
                  </div>
                  
                  {((company as any).dotProfile?.dotNumber || company.inactiveDotNumbers) && (
                    <>
                      <Separator />
                      
                      {(company as any).dotProfile?.dotNumber && (
                        <div className="flex items-center gap-3">
                          <Truck className="w-5 h-5 text-gray-500" />
                          <div>
                            <p className="font-medium">{(company as any).dotProfile?.dotNumber}</p>
                            <p className="text-sm text-gray-500">DOT Number</p>
                          </div>
                        </div>
                      )}
                      
                      {company.inactiveDotNumbers && (
                        <div className="flex items-start gap-3">
                          <Truck className="w-5 h-5 text-gray-500 mt-0.5" />
                          <div>
                            <p className="font-medium">{company.inactiveDotNumbers}</p>
                            <p className="text-sm text-gray-500">Inactive DOT Numbers</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  <Separator />
                  
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.dotPin || "Not provided"}</p>
                      <p className="text-sm text-gray-500">DOT PIN</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.loginGovUsername || "Not provided"}</p>
                      <p className="text-sm text-gray-500">Login.gov Username</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.loginGovAuthMethod ? formatLoginGovAuthMethod(company.loginGovAuthMethod) : "Not provided"}</p>
                      <p className="text-sm text-gray-500">Login.gov Authentication Method</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{company.users?.length || 0}</p>
                        <p className="text-sm text-gray-500">Users</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{company.carriers?.length || 0}</p>
                        <p className="text-sm text-gray-500">Carriers</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="contacts">
              <CompanyContactsSection
                companyId={companyId}
                companyName={company.name}
              />
            </TabsContent>
            
            <TabsContent value="carriers">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Carriers</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFMCSABulkModal(true)}
                      data-testid="button-fetch-carriers-fmcsa"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Fetch from FMCSA
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {company.carriers && company.carriers.length > 0 ? (
                    <div className="space-y-2">
                      {company.carriers.map((carrier: any) => (
                        <div key={carrier.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex-1">
                            <button
                              onClick={() => {
                                onClose();
                                setLocation(`/carriers?id=${carrier.id}`);
                              }}
                              className="font-medium text-primary hover:text-primary/80 hover:underline text-left"
                              data-testid={`link-carrier-${carrier.id}`}
                            >
                              {carrier.name}
                            </button>
                            <p className="text-sm text-gray-500">
                              DOT: {(company as any).dotProfile?.dotNumber || "Not set"} | {carrier.docketNumber ? `${carrier.docketPrefix}-${carrier.docketNumber}` : "Docket: Not set"}
                            </p>
                          </div>
                          <Badge className={carrier.commonAuthorityStatus === "ACTIVE" || carrier.commonAuthorityStatus === "A" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                            {carrier.commonAuthorityStatus || "Unknown"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No carriers found</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="work-orders">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Work Orders</CardTitle>
                    <Button 
                      size="sm" 
                      onClick={() => setShowCreateWorkOrderModal(true)}
                      data-testid="button-create-work-order"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Work Order
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {!primaryContact && company?.type !== 'service_bureau' && (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          No primary contact is set for this company. Work orders require a requesting contact. 
                          Please set a primary contact in the Contacts tab first.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Search work orders..."
                          value={workOrderSearchTerm}
                          onChange={(e) => setWorkOrderSearchTerm(e.target.value)}
                          className="pl-10"
                          data-testid="input-search-work-orders"
                        />
                      </div>
                      <Select value={workOrderStatusFilter} onValueChange={setWorkOrderStatusFilter}>
                        <SelectTrigger className="w-48" data-testid="select-status-filter">
                          <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="active">Active Only</SelectItem>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                          <SelectItem value="validating">Validating</SelectItem>
                          <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                          <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                          <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                          <SelectItem value="work_complete">Work Complete</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {filteredWorkOrders.length > 0 ? (
                      <div className="space-y-2">
                        {filteredWorkOrders.map((workOrder: any) => (
                          <div key={workOrder.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <button
                                  onClick={() => {
                                    onClose();
                                    setLocation(`/work-orders/${workOrder.id}`);
                                  }}
                                  className="font-medium text-primary hover:text-primary/80 hover:underline text-left"
                                  data-testid={`link-work-order-${workOrder.id}`}
                                >
                                  {workOrder.workOrderNumber}
                                </button>
                                <p className="text-sm text-gray-500 mt-1">
                                  {workOrder.serviceType?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>Created: {workOrder.createdAt ? format(new Date(workOrder.createdAt), 'MMM d, yyyy') : 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>Updated: {workOrder.updatedAt ? format(new Date(workOrder.updatedAt), 'MMM d, yyyy') : 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                              <Badge className={workOrder.status === "completed" ? "bg-green-100 text-green-800" : workOrder.status === "cancelled" ? "bg-gray-100 text-gray-800" : "bg-yellow-100 text-yellow-800"}>
                                {workOrder.status?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        {company.workOrders && company.workOrders.length > 0 ? "No work orders match your search" : "No work orders found"}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="notes">
              <NotesSection
                entityType="company"
                entityId={companyId.toString()}
                title="Company Notes"
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      
      {showEditModal && (
        <EditCompanyModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateCompany}
          isLoading={updateCompanyMutation.isPending}
          company={company}
        />
      )}

      <FMCSACarriersBulkModal
        open={showFMCSABulkModal}
        onClose={() => setShowFMCSABulkModal(false)}
        companyId={companyId}
        companyName={company?.name || ''}
        dotNumber={(company as any)?.dotProfile?.dotNumber || null}
        existingCarriers={company?.carriers || []}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
          queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
        }}
      />

      {showCreateWorkOrderModal && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateWorkOrderModal(false)}
          onSubmit={(data) => createWorkOrderMutation.mutate(data)}
          isLoading={createWorkOrderMutation.isPending}
          users={users}
          currentUserId={currentUser?.id}
          prePopulate={
            company?.type === 'service_bureau' 
              ? undefined // Service bureaus need to select company/carrier manually
              : primaryContact 
                ? { 
                    contactId: primaryContact.id,
                    contactName: `${primaryContact.firstName} ${primaryContact.lastName}`,
                    companyId: companyId,
                  }
                : undefined
          }
        />
      )}
    </>
  );
}